import { NgComponentOutlet, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, Type } from '@angular/core';
import { ColumnsBlock } from '../../../model';

@Component({
  selector: 'dojo-columns-block-renderer',
  template: `
    <div
      class="columns-layout"
      [class.columns-3]="block().columns.length === 3"
      [class.show-borders]="block().show_borders"
      [ngStyle]="columnWidthVars()"
    >
      @for (col of block().columns; track $index) {
        <div class="column">
          <ng-container *ngComponentOutlet="contentRenderer(); inputs: { blocks: col.content }" />
        </div>
      }
    </div>
  `,
  styles: `
    $breakpoint-stack: 600px;
    $breakpoint-wrap-3col: 640px;

    :host {
      display: block;
      container-type: inline-size;
    }

    .columns-layout {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: flex-start;
      justify-content: center;
    }

    .column ::ng-deep > * > :first-child > :first-child,
    .column ::ng-deep > * > :first-child > :first-child > :first-child {
      margin-top: 0 !important;
      margin-block-start: 0 !important;
    }
    .column ::ng-deep > * > :last-child > :last-child,
    .column ::ng-deep > * > :last-child > :last-child > :last-child {
      margin-bottom: 0 !important;
      margin-block-end: 0 !important;
    }

    .columns-layout > .column:nth-child(1) {
      flex: var(--col-1-flex, 1) 1 0%;
      min-width: 0;
    }

    .columns-layout > .column:nth-child(2) {
      flex: var(--col-2-flex, 1) 1 0%;
      min-width: 0;
    }

    .columns-layout > .column:nth-child(3) {
      flex: 1 1 0%;
      min-width: 0;
    }

    /* Strip outer padding so content is flush with block edges */
    .columns-layout > .column:first-child {
      padding-left: 0;
    }

    .columns-layout > .column:last-child {
      padding-right: 0;
    }

    /* Vertical divider lines between columns */
    .columns-layout.show-borders > .column:not(:last-child) {
      border-right: 1px solid var(--neutral-200);
      padding-right: 1rem;
    }

    /* 3-column intermediate: force third column to wrap below at full width */
    @container (max-width: #{$breakpoint-wrap-3col}) {
      .columns-layout.columns-3 > .column {
        min-width: calc(50% - 0.5rem);
      }

      .columns-layout.columns-3.show-borders > .column:nth-child(2) {
        border-right: none;
        padding-right: 0;
      }
    }

    /* Narrow: stack all columns vertically — reset custom widths */
    @container (max-width: #{$breakpoint-stack}) {
      .columns-layout > .column:nth-child(1),
      .columns-layout > .column:nth-child(2),
      .columns-layout.columns-3 > .column {
        --col-1-flex: 1;
        --col-2-flex: 1;
        min-width: 100%;
      }

      .columns-layout > .column {
        padding-left: 0;
        padding-right: 0;
      }

      .columns-layout.show-borders > .column:not(:last-child) {
        border-right: none;
        padding-right: 0;
        border-bottom: 1px solid var(--neutral-200);
        padding-bottom: 1rem;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, NgStyle],
})
export class ColumnsBlockRendererComponent {
  readonly block = input.required<ColumnsBlock>();
  readonly contentRenderer = input.required<Type<unknown>>();

  readonly columnWidthVars = computed(() => {
    const widths = this.block().column_widths;
    if (widths && widths.length === 2) {
      return {
        '--col-1-flex': String(widths[0]),
        '--col-2-flex': String(widths[1]),
      };
    }
    return {};
  });
}
